import { FC, useEffect, useMemo, useState } from 'react';
import Drawer from '..';
import DrawerHeader from '../header';
import DrawerContent from '../content';
import { useQuery } from 'react-query';
import useViewMode from '@/hooks/use-view-mode.hook';
import useQueryHelper from '@/hooks/use-query-helper';
import useFormHandle from '@/hooks/use-form-handle.hook';
import { ConnectorType } from '@/lib/models/connector';
import InputControl from '../../form/input-control';
import useToast from '@/hooks/use-toast.hook';
import useModal from '@/hooks/use-modal.hook';
import DrawerButtons from '@/components/form/drawer-buttons';
import { getSiemConnectors } from '@/lib/services/connector.service';
import { QueryKey, refetchQuery } from '@/lib/query-client';
import { Form } from '@/components/form';
import useFeature from '@/hooks/use-feature.hook';
import TelemetryMenu from './menu';
import { getTypedTelemetry } from '@/lib/models/telemetry';
import { TelemetryModel } from '@/lib/models/telemetry/telemetry.model';
import {
  createTelemetry,
  deleteTelemetry,
  getSettings,
  getTelemetryById,
  patchTelemetry,
} from '@/lib/services/telemetry.service';
import AwsS3Telemetry from './aws-s3-telemetry';
import { AwsS3TelemetryModel } from '@/lib/models/telemetry/aws-s3-telemetry.model';
import { FieldError } from '@/lib/services';
import TelemetryReadonlyView from './readonly-view';
import TelemetryTestCard from './test-card';
import { Box } from '@mui/material';
import { ReadonlySection } from '@/components/form/readonly-view/section';
import { DrawerProps } from '../helpers';
import NoneChip from '@/components/chip/none-chip';
import TelemetryTypes from './telemetry-types';
import HttpTelemetry from './http-telemetry';
import { HttpTelemetryModel } from '@/lib/models/telemetry/http-telemetry.model';
import SplunkTelemetry from './splunk-telemetry';
import { SplunkTelemetryModel } from '@/lib/models/telemetry/splunk-telemetry.model';
import CrowdStrikeTelemetry from './crowdstrike-telemetry';
import { CrowdStrikeTelemetryModel } from '@/lib/models/telemetry/crowdstrike-telemetry.model';
import DatadogTelemetry from './datadog-telemetry';
import { DatadogTelemetryModel } from '@/lib/models/telemetry/datadog-telemetry.model';

interface Props extends DrawerProps {
  id: string | null;
  connectorType?: ConnectorType;
  name?: string;
}

const formModel = new TelemetryModel({
  telemetryTypes: {
    userEvents: { enabled: true, schemaVersion: 'v2' },
    auditEvents: { enabled: true, schemaVersion: 'v1' },
  },
});

export const TelemetryDrawer: FC<Props> = ({ id, open, onClose, isTopDrawer }) => {
  const [telemetryId, setTelemetryId] = useState(id || null);
  const isNew = !telemetryId;
  const { setMode, inWriteMode } = useViewMode(isNew ? 'add' : 'view');

  const { toast, errorToast } = useToast();
  const { openModal } = useModal();
  const { canChangeTelemetry } = useFeature();

  const query = useQuery<TelemetryModel | null>(
    [QueryKey.TelemetryView, telemetryId],
    async () => getTelemetryById(telemetryId!),
    {
      enabled: !isNew,
    }
  );

  const { showLoader: telemetryLoading } = useQueryHelper(query);
  const { data: telemetry, refetch } = query;

  const connectorQuery = useQuery([QueryKey.ConnectorSiemsList, id], async () =>
    getSiemConnectors()
  );
  const { showLoader: connectorsLoading } = useQueryHelper(connectorQuery);
  const { data: connectors = [] } = connectorQuery;

  const settingsQuery = useQuery([QueryKey.TelemetrySettings], async () => getSettings());
  const { showLoader: settingsLoading } = useQueryHelper(settingsQuery);
  const { data: settings } = settingsQuery;

  const handleRefresh = () => {
    refetchQuery(QueryKey.TelemetryDataset);
  };

  const formHandle = useFormHandle<TelemetryModel>(
    {
      initialValues: formModel,
      validationSchema: formModel.schema,
      validateOnChange: false,
      onSubmit: async (telemetry, { setFieldError, resetForm }) => {
        const setFieldErrors = (fieldErrors: FieldError[]) => {
          fieldErrors.forEach(({ msg, path }) => setFieldError(path, msg));
        };

        if (!telemetryId) {
          const { fieldErrors, error, created, model } = await createTelemetry(telemetry);

          if (fieldErrors) {
            setFieldErrors(fieldErrors);
            return;
          }

          if (error || !created) {
            errorToast(error || 'The telemetry destination was not created');
            return;
          }

          resetForm({ values: telemetry });
          setTelemetryId(model?.id || '');
          toast('The telemetry destination was created');
          handleRefresh();
          setMode('view');
          return;
        }

        const { patched, fieldErrors, error } = await patchTelemetry(telemetry);

        if (fieldErrors) {
          setFieldErrors(fieldErrors);
          return;
        }

        if (error || !patched) {
          errorToast(error || 'The telemetry destination was not updated');
          return;
        }

        resetForm({ values: telemetry });
        toast('The telemetry destination was updated');
        refetch();
        handleRefresh();
        setMode('view');
      },
    },
    { lockTarget: 'drawer' }
  );

  const {
    handleSubmit,
    handleBlur,
    canSubmit,
    isSubmitting,
    loadData,
    values,
    updateValidationSchema,
    setValues,
  } = formHandle;

  const telemetryType = values.type;

  const handleCancel = () => {
    if (isNew) {
      onClose(true);
      return;
    }
    setMode('view');
    loadData(telemetry || new TelemetryModel());
  };

  const handleEdit = () => {
    setMode('edit');
  };

  const handleDelete = () => {
    openModal('confirm', {
      title: 'Confirm Delete',
      content: `Are you sure you want to delete ${telemetry?.displayName}? This action cannot be undone.`,
      onClose: (confirm: boolean) => {
        if (confirm) {
          deleteTelemetry(telemetry!.id).then(({ deleted, error }) => {
            if (!deleted) {
              errorToast(error || 'The telemetry destination was not deleted');
              return;
            }

            setTelemetryId('');
            onClose();
            toast('The telemetry destination was deleted');
            handleRefresh();
          });
        }
      },
    });
  };

  const handleTest = () => {
    refetch();
    handleRefresh();
  };

  const handleChangeType = (newType: string) => {
    if (!isNew) {
      return;
    }

    const typedModel = getTypedTelemetry(
      new TelemetryModel({ ...values._props, type: newType, config: {} })
    );
    setValues(typedModel);
    updateValidationSchema(typedModel.schema);
  };

  const connector = useMemo(
    () => connectors?.find(({ id }) => id === values.connectorId),
    [connectors, values.connectorId]
  );

  useEffect(() => {
    if (telemetry) {
      loadData(telemetry);
      updateValidationSchema(telemetry.schema);
      setTelemetryId(telemetry.id);
    }
  }, [telemetry, loadData, updateValidationSchema]);

  const isLoading = telemetryLoading || connectorsLoading || settingsLoading;
  const hasTelemetry = !!telemetry?.id;
  const canDelete = canChangeTelemetry;
  const canEdit = (!connector || connector?.status === 'active') && canChangeTelemetry;
  const canTest = telemetry?.canTest && !isNew && !inWriteMode;
  const hasType = !!telemetryType;

  return (
    <Drawer open={open} onClose={onClose} loading={isLoading}>
      <DrawerHeader
        onClose={onClose}
        menu={
          <TelemetryMenu
            telemetry={telemetry || null}
            settings={settings}
            showUpdateMenu={!isNew && canChangeTelemetry}
            onEdit={handleEdit}
            onDelete={handleDelete}
            canEdit={!inWriteMode && canEdit}
            canDelete={!inWriteMode && canDelete}
            editSubtitle={
              canEdit
                ? ''
                : 'You cannot edit a telemetry destination if the associated connector is not active.'
            }
            deleteSubtitle={
              canDelete ? '' : 'You cannot delete a telemetry destination that is in use'
            }
          />
        }
        isTopDrawer={isTopDrawer}
      >
        {isNew ? 'Add Telemetry Destination' : telemetry?.displayName}
      </DrawerHeader>
      <DrawerContent>
        <Box mt={2}>
          {!inWriteMode && hasTelemetry && (
            <TelemetryReadonlyView
              connector={connector}
              telemetry={telemetry}
              settings={settings!}
            />
          )}

          {inWriteMode && (
            <Form formHandle={formHandle}>
              <InputControl name="displayName" label="Name" formHandle={formHandle} autoFocus />

              <InputControl
                name="type"
                label="Destination Type"
                type="telemetry-type-select"
                formHandle={formHandle}
                readonly={!isNew}
                onBlur={handleBlur}
                onChange={handleChangeType}
              />

              {telemetryType === 's3' && (
                <AwsS3Telemetry
                  formHandle={formHandle}
                  telemetry={telemetry as AwsS3TelemetryModel}
                  settings={settings!}
                  connectors={connectors}
                  isNew={isNew}
                />
              )}

              {telemetryType === 'http' && (
                <HttpTelemetry
                  formHandle={formHandle}
                  telemetry={telemetry as HttpTelemetryModel}
                  isNew={isNew}
                />
              )}

              {telemetryType === 'splunk' && (
                <SplunkTelemetry
                  formHandle={formHandle}
                  telemetry={telemetry as SplunkTelemetryModel}
                  isNew={isNew}
                />
              )}

              {telemetryType === 'crowdstrike' && (
                <CrowdStrikeTelemetry
                  formHandle={formHandle}
                  telemetry={telemetry as CrowdStrikeTelemetryModel}
                  isNew={isNew}
                />
              )}

              {telemetryType === 'datadog' && (
                <DatadogTelemetry
                  formHandle={formHandle}
                  telemetry={telemetry as DatadogTelemetryModel}
                  isNew={isNew}
                />
              )}

              <Box mt={3}>
                {hasType && (
                  <>
                    <TelemetryTypes formHandle={formHandle} />
                    <InputControl
                      name="enabled"
                      label="Enabled"
                      type="switch"
                      formHandle={formHandle}
                      inline={false}
                    />
                  </>
                )}
              </Box>
              <DrawerButtons
                submit
                cancel
                isNew={isNew}
                canSubmit={canSubmit}
                canCancel={!isSubmitting}
                onSubmit={handleSubmit}
                onCancel={handleCancel}
                formHandle={formHandle}
              />
            </Form>
          )}

          {!isNew && !inWriteMode && (
            <Box mt={2}>
              <ReadonlySection label="Testing" />
              {canTest ? (
                <TelemetryTestCard telemetry={telemetry} onTest={handleTest} />
              ) : (
                <Box mt={1}>
                  <NoneChip notAvailable />
                </Box>
              )}
            </Box>
          )}
        </Box>
      </DrawerContent>
    </Drawer>
  );
};

export default TelemetryDrawer;
